import React, { useEffect, useState } from "react";
import ErrorBoundary from "./ErrorBoundary";
import { User } from "@microsoft/microsoft-graph-types";
import { IBaseQueryParams, IOperationRequestBase, IPhoneNumber, PhoneNumberAssignmentAction, PhoneNumberClassification, PstnAssignmentStatus, PhoneNumberType, IBaseQueryResult, TaskStatus, ITeamsPowerShellTask, teamsPowerShellTaskSchema, TeamsPowerShellTaskType } from "@symity-hub/types";
import { sharedVerticalMediumGapFlexStyles } from "../styles/styles";
import { fetcher } from "../modules/api";
import { useMsal } from "@azure/msal-react";
import useSWR from "swr";
import { ChoosePhoneNumber } from "./ChoosePhoneNumber";
import { CustomAlert } from "./CustomAlert";
import { OperationDialog } from "./OperationDialog";
import { getOptionsFromEnum } from "../modules/enum";
import { IQueryProps } from "../types/IQueryProps";
import { IDropdownFilter } from "../types/IDropdownFilter";
import { renderPhoneNumberAssignmentText, renderPhoneNumberWithExtension } from "../modules/string";
import { CustomEditDrawer } from "./CustomEditDrawer";
import { TableColumnDefinition, createTableColumn, TableCellLayout, Button, Spinner } from "@fluentui/react-components";
import { BasicDataGrid } from "./BasicDataGrid";
import { Edit24Regular } from "@fluentui/react-icons";
import { ZodError } from "zod";
import { populateDropdownFilter } from "./DropdownFilters";

interface IAssignPhoneNumberPanelProps {
    users: User[];
    open: boolean;
    setOpen: (open: boolean) => void;
    onDismiss: () => void
}

export const AssignPhoneNumberPanel: React.FunctionComponent<IAssignPhoneNumberPanelProps> = (props) => {

    const msalContext = useMsal();

    const [tasks, setTasks] = useState<ITeamsPowerShellTask[]>([]);
    const [userToAssign, setUserToAssign] = useState<User | undefined>(undefined);
    const [operationId, setOperationId] = useState<string>();
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<ZodError>();

    // Store query params outside of choose number component so it persists between user selection
    const defaultQueryParams: IBaseQueryParams = {
        sortBy: "phoneNumber",
        limit: 8,
        filters: [
            {
                name: "pstnAssignmentStatus",
                operator: "eq",
                value: PstnAssignmentStatus.Unassigned
            },
            {
                name: "classification",
                operator: "eq",
                value: PhoneNumberClassification.Standard
            }
        ]
    };

    const defaultDropdownFilters: IDropdownFilter[] = [
        {
            name: "rangeId",
            label: "Phone number range",
            options: [],
            selectedKeys: [],
            multiSelect: true
        },
        {
            name: "classification",
            label: "Classification",
            options: getOptionsFromEnum(PhoneNumberClassification),
            selectedKeys: [
                PhoneNumberClassification.Standard
            ],
            multiSelect: true
        },
        {
            name: "numberType",
            label: "Type",
            options: getOptionsFromEnum(PhoneNumberType),
            selectedKeys: [],
            multiSelect: true
        }
    ];

    const [queryProps, setQueryProps] = useState<IQueryProps>({
        params: defaultQueryParams,
        searchField: "phoneNumber",
        searchPlaceholderText: "Search by phone number"
    });

    const [dropdownFilters, setDropdownFilters] = useState<IDropdownFilter[]>(defaultDropdownFilters);

    // Get any existing phone number assignments
    const { data, error, mutate, isLoading } = useSWR<IPhoneNumber[]>(props.users.length > 0 && props.open ? [`/api/voice/phoneNumbers/users/assigned`, msalContext, "POST", undefined, { userIds: props.users.map((user) => user.id) }] : null, fetcher,
        {
            revalidateIfStale: false,
            revalidateOnFocus: false,
            onSuccess: (data) => {
                // Filter on user phone numbers that have assigned pstn target id set
                setTasks(data.filter((item) => item.userId).map((phoneNumber) => {
                    return {
                        name: renderPhoneNumberAssignmentText(PhoneNumberAssignmentAction.Assign, phoneNumber.phoneNumber, phoneNumber.extensionNumber, props.users.find((user) => user.id === phoneNumber.userId)?.displayName || ""),
                        type: TeamsPowerShellTaskType.PhoneNumberAssignment,
                        phoneNumberAssignment: {
                            phoneNumber: phoneNumber.phoneNumber,
                            extensionNumber: phoneNumber.extensionNumber,
                            userId: phoneNumber.userId!,
                            phoneNumberType: phoneNumber.numberType,
                            action: PhoneNumberAssignmentAction.Assign
                        }
                    }
                }));
            }
        }
    );

    useSWR<IBaseQueryResult>([`/api/voice/phoneNumberRanges`, msalContext, undefined, {
        sortBy: "name",
        select: ["id", "name"],
        limit: 100,
    }], fetcher, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        onSuccess: (data) => {
            populateDropdownFilter(data, dropdownFilters, setDropdownFilters, "rangeId", "id", "name");
        }
    });

    const onSave = async () => {
        if (tasks.length > 0) {
            // Validate schema
            const validTasks = await Promise.all(tasks.map(async (assignment) => {
                return await teamsPowerShellTaskSchema.parseAsync(assignment)
                    .then(async () => {
                        return assignment;
                    })
            })).catch((error: ZodError) => {
                console.error(error.errors);
                setValidationError(error);
            });
            if (validTasks && validTasks.length > 0 && !validationError) {
                // Filter out unassign actions if user has an assign action (it overwrites the unassign)
                const filteredTasks = validTasks.filter((assignment) => {
                    if (assignment?.phoneNumberAssignment?.action === PhoneNumberAssignmentAction.Unassign) {
                        return !validTasks.some((a) => a?.phoneNumberAssignment?.userId === assignment?.phoneNumberAssignment?.userId && a?.phoneNumberAssignment?.action === PhoneNumberAssignmentAction.Assign);
                    }
                    return true;
                });
                if (filteredTasks.length > 0) {
                    // Create operation request
                    const operationRequest: IOperationRequestBase = {
                        tasks: filteredTasks
                    }
                    // Send request
                    await fetcher(["/api/voice/phoneNumbers/assign", msalContext, "POST", undefined, operationRequest])
                        .then((data) => {
                            // If successful, set the operation ID and open the dialog
                            if (data.operationId) {
                                setOperationId(data.operationId);
                                setDialogOpen(true);
                            }
                        })
                }
            }
        }
    }

    // Refresh data when panel is opened
    useEffect(() => {
        if (props.open) {
            mutate();
        }
    }, [mutate, props.open]);

    const onDismiss = () => {
        // Reset state
        setTasks([]);
        setUserToAssign(undefined);
        setQueryProps({
            ...queryProps,
            params: defaultQueryParams
        });
        setDropdownFilters(defaultDropdownFilters);
        props.onDismiss();
    }

    const onDismissOperationDialog = () => {
        setDialogOpen(false);
        setOperationId(undefined);
        onDismiss();
    }

    // Calculate which numbers require an unassign action
    useEffect(() => {
        // Calculate numbers in data but not against assignment of users
        const numbersInDataNotInTasks = data?.filter((item) => item.userId).map((item) => item.phoneNumber).filter((item) => !tasks.map((item) => item.phoneNumberAssignment?.phoneNumber).includes(item));
        const itemsInDataNotInTasks = data?.filter((item) => numbersInDataNotInTasks?.includes(item.phoneNumber));
        // Add missing items to unassign assignments
        if (itemsInDataNotInTasks && itemsInDataNotInTasks.length > 0) {
            setTasks([...tasks, ...itemsInDataNotInTasks.map((phoneNumber) => {
                return {
                    name: renderPhoneNumberAssignmentText(PhoneNumberAssignmentAction.Unassign, phoneNumber.phoneNumber, phoneNumber.extensionNumber, props.users.find((user) => user.id === phoneNumber.userId)?.displayName || ""),
                    type: TeamsPowerShellTaskType.PhoneNumberAssignment,
                    phoneNumberAssignment: {
                        phoneNumber: phoneNumber.phoneNumber,
                        extensionNumber: phoneNumber.extensionNumber,
                        userId: phoneNumber.userId!,
                        phoneNumberType: phoneNumber.numberType,
                        action: PhoneNumberAssignmentAction.Unassign
                    }
                }
            })]);
        }
    }, [data, tasks, props.users]);

    const columns: TableColumnDefinition<User>[] = [
        createTableColumn<User>({
            columnId: "displayName",
            renderHeaderCell: () => {
                return "Name";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        {item.displayName}
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<User>({
            columnId: "phoneNumber",
            renderHeaderCell: () => {
                return "Phone number";
            },
            renderCell: (item) => {
                const task = tasks.find(task => task.phoneNumberAssignment?.userId === item.id && task.phoneNumberAssignment?.action !== PhoneNumberAssignmentAction.Unassign);
                return (
                    <TableCellLayout>
                        {task?.phoneNumberAssignment?.phoneNumber ? renderPhoneNumberWithExtension(task.phoneNumberAssignment?.phoneNumber, task.phoneNumberAssignment?.extensionNumber) : "No phone number assigned"}
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<User>({
            columnId: "phoneNumberType",
            renderHeaderCell: () => {
                return "Phone number type";
            },
            renderCell: (item) => {
                const task = tasks.find(task => task.phoneNumberAssignment?.userId === item.id && task.phoneNumberAssignment?.action !== PhoneNumberAssignmentAction.Unassign);
                return (
                    <TableCellLayout>
                        {task?.phoneNumberAssignment?.phoneNumberType ? task.phoneNumberAssignment?.phoneNumberType : ""}
                    </TableCellLayout>
                );
            }
        }),
        createTableColumn<User>({
            columnId: "actions",
            renderHeaderCell: () => {
                return "";
            },
            renderCell: (item) => {
                return (
                    <TableCellLayout>
                        <Button
                            icon={<Edit24Regular />}
                            onClick={() => { setUserToAssign(item) }}
                        >
                            Edit
                        </Button>
                    </TableCellLayout>
                );
            }
        })
    ];

    return (
        <CustomEditDrawer
            open={props.open}
            onDismiss={onDismiss}
            onSave={onSave}
            headerText="Assign phone numbers"
            validationError={validationError}
            size="large"
        >
            <ErrorBoundary>
                <div className={sharedVerticalMediumGapFlexStyles().root}>
                    {error && (<CustomAlert text={error} type={TaskStatus.Error} id="assignPhoneNumberPanel" />)}
                    {isLoading
                        ? <Spinner size="large" />
                        : data && props.users.length > 0 && !userToAssign && (
                            <BasicDataGrid
                                columns={columns}
                                items={props.users}
                            />
                        )}
                    {userToAssign && (
                        <ChoosePhoneNumber
                            queryProps={queryProps}
                            setQueryProps={setQueryProps}
                            dropdownFilters={dropdownFilters}
                            setDropdownFilters={setDropdownFilters}
                            tasks={tasks}
                            setTasks={setTasks}
                            userToAssign={userToAssign}
                            setUserToAssign={setUserToAssign}
                        />
                    )}
                    <OperationDialog
                        open={dialogOpen}
                        onDismiss={onDismissOperationDialog}
                        operationId={operationId} />
                </div>
            </ErrorBoundary>
        </CustomEditDrawer>
    );
};
